<script setup lang="ts">
import BackgroundHomeSectionExclusive from '~/components/effects/BackgroundHomeSectionExclusive.vue'

defineOptions({
	name: 'HomeExclusive',
})

const route = useRoute()
const { t: $t, locale } = useI18n()

const benefits = computed(() => {
	return [
		{
			title: $t('Unlock the potential of your numbers'),
			description: $t(
				'Ever wondered if your favorite numbers have been winners in the past? Our tool analyzes historical results to show you how your combination has performed over time',
			),
		},
		{
			title: $t('Simple and easy to use'),
			description: $t(`No complicated rules or confusing stats! Just enter numbers and see your potential wins`),
		},
		{
			title: $t('Unlock winning patterns'),
			description: $t(`Discover the hot numbers, cold numbers, and trends for increased odds in upcoming draws`),
		},
		{
			title: $t('Reliable real-time results'),
			description: $t(
				`All information is sourced directly from official lottery providers in real time, ensuring accuracy and transparency in every result`,
			),
		},
	]
})

const getImage = computed(() => {
	if (locale.value === 'pt') return 'iphone_pt.webp'
	if (locale.value === 'fr') return 'iphone_img_fr.webp'
	return 'iphone.webp'
})
</script>

<template>
	<section class="pt-14 sm:pt-24 pb-14 sm:pb-28 relative z-[5]">
		<BackgroundHomeSectionExclusive />
		<div class="container space-y-12 sm:space-y-10">
			<div class="space-y-14 sm:space-y-7">
				<h2
					class="text-center md:text-left text-secondary-300 font-semibold text-xl sm:text-[40px] leading-10 md:leading-9"
				>
					<span class="text-primary-500">{{ $t('Exclusive features') }}</span>
					{{ $t('to enhance your experience') }}
				</h2>
				<div class="flex 2xl:block flex-col relative 2xl:min-h-[850px]">
					<div
						class="2xl:max-w-md pt-20 sm:pt-12 md:border-t border-neutral-100 grid grid-cols-1 smm:grid-cols-2 2xl:grid-cols-1 gap-16 mx-auto 2xl:mx-0"
					>
						<div
							v-for="(benefit, index) in benefits"
							:key="index"
							class="flex flex-col gap-4 sm:max-w-80"
						>
							<h3 class="text-xl font-semibold text-white">{{ benefit.title }}</h3>
							<p class="text-neutral-100">{{ benefit.description }}.</p>
						</div>
					</div>

					<NuxtImg
						:src="`/cdn/${getImage}`"
						alt="iPhone"
						format="webp"
						quality="80"
						densities="1x 2x"
						sizes="(max-width: 1279px) 320px, 700px"
						preload
						loading="eager"
						fetchpriority="high"
						class="block xl:hidden order-first xl:order-last w-4/5 object-contain object-center select-none pointer-events-none mx-auto max-h-[450px] sm:max-h-[570px] 2xl:max-h-[850px] h-full scale-[1.4] smm:scale-125 sm:scale-100"
					/>
					<NuxtImg
						:src="`/cdn/${getImage}`"
						alt="iPhone"
						format="webp"
						quality="90"
						class="hidden xl:block 2xl:absolute 2xl:right-0 2xl:top-0 w-4/5 2xl:w-full object-contain object-center 2xl:object-[right_top] select-none pointer-events-none transition-opacity duration-200 scale-[1.4] smm:scale-125 sm:scale-100 order-first 2xl:order-last max-h-[450px] sm:max-h-[570px] 2xl:max-h-[850px] h-full mx-auto 2xl:mx-0"
					/>
				</div>
			</div>

			<div class="rounded-2xl bg-primary-700/[0.7] relative overflow-hidden">
				<NuxtImg
					src="/cdn/background-coins.webp"
					alt="Coins background"
					format="webp"
					quality="85"
					class="absolute inset-0 h-full w-full object-cover object-left-bottom sm:object-center pointer-events-none select-none"
				/>
				<div class="absolute inset-0 bg-primary-700/70 rounded-2xl z-0" />
				<div class="relative z-10 flex flex-col gap-14 sm:gap-8 items-center py-20 sm:py-8 px-2 sm:px-8">
					<div class="text-white text-center mx-auto max-w-2xl space-y-8 px-10 sm:px-0">
						<h2 class="font-semibold text-xl sm:text-3xl">{{ $t('Discover your hidden wins') }}</h2>
						<p class="text-[18px] sm:text-xl">
							{{ $t('Check your past numbers with real-time, official results and see if luck was on your side') }}.
						</p>
					</div>
					<UButton
						:to="{ path: route.path, query: route.query, hash: '#hero' }"
						color="white"
						variant="soft"
						name="label"
						trailing
						:label="$t('Check past results')"
						:ui="{
							rounded: 'rounded-full',
							base: 'xs:whitespace-nowrap',
						}"
						class="px-6 min-h-12 text-base font-semibold flex justify-center hover:bg-opacity-90 bg-primary-50 border-none text-success-600"
					>
						<template #leading>
							<UIcon
								name="i-mage:stars-c"
								class="w-6 h-6"
							/>
						</template>
					</UButton>
				</div>
			</div>
		</div>
	</section>
</template>
